/**
 * 公開申込フォームとオプトイン証跡の zod 契約。
 * `docs/CMS_DESIGN.md` の D23 / D27 / D31 と §5.2 が唯一の正。
 *
 * この面が守る不変条件は 4 つある。
 *
 * 1. **同意文言の本体はフォームの版に不変で残り、証跡は sha256 で指すだけ。**
 *    10 万件の `optin_records` に同じ 500 文字を複製しない。争いになったときに
 *    必要なのは「どの文言に同意したか」であって、その文言のコピーの数ではない。
 * 2. **生 IP と生 User-Agent を保存しない。** 残すのは `/24` に丸めた ip_prefix と
 *    UA の sha256 + family だけ（D23）。UA 全文は fingerprint 材料になり、
 *    個人情報保護法上の識別子になりうる。
 * 3. **公開フォームの脅威は CSRF ではなく list-bombing**（D31）。
 *    `allowedOrigins` は空配列を「自オリジンのみ」と解釈し、
 *    **`*` を表現できる形を型として持たない**。
 * 4. **確認メールを送るまで購読状態にしない。** `double_optin` が既定で、
 *    off にできるのは `signup_forms.double_optin=false` を明示した組織だけ。
 *
 * ここは公開面（認証なし）の契約でもあるので、**エラーの粒度をわざと落とす**。
 * 「そのアドレスは登録済みです」を返すと、フォームがアドレス存在確認の
 * オラクルになる（第三者が任意のアドレスの登録状況を調べられる）。
 */
import { z } from 'zod';
import {
  CONTACT_OPTIN_METHOD,
  SIGNUP_CHALLENGE_MODE,
  SIGNUP_FORM_STATUS,
  SIGNUP_SUBMISSION_STATE,
} from '../enums.js';
import { integrationDateTimeSchema, integrationIdSchema } from './content-api.js';

const id = integrationIdSchema;
const isoDateTime = integrationDateTimeSchema;

/* ============================================================ フォームの定義 */

/**
 * フォームに置ける入力欄。**自由な HTML は置けない。**
 *
 * `kind` を閉じた列挙にしてあるのがこの型の要点で、
 * 「任意の HTML を差し込める欄」を 1 つ作った瞬間に、
 * 公開ページが任意 HTML ホスティング（CD §7 の禁止事項）になる。
 */
export const SIGNUP_FIELD_KIND = ['email', 'text', 'select', 'checkbox'] as const;
export type SignupFieldKind = (typeof SIGNUP_FIELD_KIND)[number];

export const signupFieldSchema = z
  .object({
    kind: z.enum(SIGNUP_FIELD_KIND),
    /**
     * 保存先の属性キー。`contact_lists.attribute_schema` に宣言済みのキーだけを許す
     * （検査はアプリ層。ここで宣言を参照すると契約が DB に依存してしまう）。
     * `email` 欄だけは予約語 `email` を使う。
     */
    key: z
      .string()
      .min(1)
      .max(60)
      .regex(/^[a-z][a-z0-9_]*$/, '属性キーは英小文字で始まり、英小文字・数字・`_` のみです'),
    label: z.string().min(1).max(120),
    required: z.boolean().default(false),
    /** `select` のときの選択肢。他の kind では空配列。 */
    options: z.array(z.string().min(1).max(120)).max(50).default([]),
    /** 入力例。プレースホルダに出す。 */
    placeholder: z.string().max(120).optional(),
  })
  .strict();
export type SignupField = z.infer<typeof signupFieldSchema>;

/**
 * 法定フッタのスナップショット（特定電子メール法の表示義務）。
 * フォームの版に**焼き付ける**。後からリスト側の設定を直しても、
 * 過去の同意が「どの表示を見て行われたか」は動かない。
 */
export const legalFooterSnapshotSchema = z
  .object({
    senderName: z.string().min(1).max(200),
    address: z.string().min(1).max(400),
    unsubscribeContact: z.string().min(1).max(400),
    complaintContact: z.string().min(1).max(400),
  })
  .strict();
export type LegalFooterSnapshot = z.infer<typeof legalFooterSnapshotSchema>;

/** 同意文言のハッシュ。`content-api.ts` の `contentHashSchema` と同じ表記規約。 */
export const consentTextHashSchema = z
  .string()
  .regex(/^sha256:[0-9a-f]{64}$/, '同意文言のハッシュは sha256:<64 桁の小文字 hex> の形式です');

/* ------------------------------------------------------------ フォーム本体 */

export const signupFormSchema = z
  .object({
    id,
    listId: id,
    slug: z.string(),
    name: z.string(),
    status: z.enum(SIGNUP_FORM_STATUS),
    currentVersionId: id.nullable(),
    currentVersion: z.number().int().positive().nullable(),
    doubleOptin: z.boolean(),
    /** **空配列 = 自オリジンのみ。** `*` を表現できる値は無い（D31）。 */
    allowedOrigins: z.array(z.string()),
    challengeMode: z.enum(SIGNUP_CHALLENGE_MODE),
    dailySubmitCap: z.number().int().positive(),
    createdAt: isoDateTime,
    updatedAt: isoDateTime,
  })
  .strict();
export type SignupFormView = z.infer<typeof signupFormSchema>;

/**
 * 許可オリジン。**スキーム付きの絶対オリジンのみ**で、パスもワイルドカードも持てない。
 * `https://*.example.com` を許すと、サブドメイン乗っ取り 1 件で
 * list-bombing の踏み台が完成する。
 */
export const allowedOriginSchema = z
  .string()
  .max(255)
  .refine((value) => {
    try {
      const url = new URL(value);
      return (
        (url.protocol === 'https:' || url.protocol === 'http:') &&
        url.pathname === '/' &&
        url.search === '' &&
        url.hash === '' &&
        !url.hostname.includes('*')
      );
    } catch {
      return false;
    }
  }, '許可オリジンは https://example.com の形式で指定してください（パス・ワイルドカードは使えません）');

export const createSignupFormSchema = z
  .object({
    listId: id,
    name: z.string().min(1).max(200),
    slug: z
      .string()
      .min(1)
      .max(60)
      .regex(/^[a-z0-9][a-z0-9-]*$/, 'slug は英小文字・数字・`-` で指定してください'),
    /** 既定 true。false にできるのは組織の設定で明示的に許可された場合のみ。 */
    doubleOptin: z.boolean().default(true),
    allowedOrigins: z.array(allowedOriginSchema).max(20).default([]),
    challengeMode: z.enum(SIGNUP_CHALLENGE_MODE).default('auto'),
    dailySubmitCap: z.number().int().positive().max(100_000).default(200),
  })
  .strict();

export const updateSignupFormSchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    doubleOptin: z.boolean().optional(),
    allowedOrigins: z.array(allowedOriginSchema).max(20).optional(),
    challengeMode: z.enum(SIGNUP_CHALLENGE_MODE).optional(),
    dailySubmitCap: z.number().int().positive().max(100_000).optional(),
    status: z.enum(['published', 'paused', 'archived']).optional(),
  })
  .strict();

/* ------------------------------------------------------------- 版の作成 */

/**
 * フォームの版。**published 後は 1 バイトも変えられない**
 * （DB トリガ `t85_signup_form_versions_immutable` が UPDATE / DELETE を拒否する）。
 * 変更は新しい版を作る操作であって、過去の版の書き換えではない。
 */
export const signupFormVersionSchema = z
  .object({
    id,
    formId: id,
    version: z.number().int().positive(),
    fields: z.array(signupFieldSchema),
    consentText: z.string(),
    consentTextSha256: consentTextHashSchema,
    privacyUrl: z.string().nullable(),
    legalFooterSnapshot: legalFooterSnapshotSchema,
    redirectUrl: z.string().nullable(),
    publishedAt: isoDateTime.nullable(),
    createdAt: isoDateTime,
  })
  .strict();

export const createSignupFormVersionSchema = z
  .object({
    /** `email` 欄をちょうど 1 つ含むこと。宛先の無い申込フォームは成立しない。 */
    fields: z
      .array(signupFieldSchema)
      .min(1)
      .max(30)
      .refine(
        (fields) => fields.filter((f) => f.kind === 'email').length === 1,
        'メールアドレスの入力欄をちょうど 1 つ置いてください',
      )
      .refine(
        (fields) => new Set(fields.map((f) => f.key)).size === fields.length,
        '属性キーが重複しています',
      ),
    /**
     * 同意文言。**空を許さない。** 「同意した」とだけ記録されていて
     * 何に同意したのかが残っていない証跡は、争いになったときに役に立たない。
     */
    consentText: z.string().min(10).max(4000),
    privacyUrl: z.string().url().max(2000).optional(),
    legalFooterSnapshot: legalFooterSnapshotSchema,
    redirectUrl: z.string().url().max(2000).optional(),
  })
  .strict();

/* ==================================================== 公開面（認証なし）の submit */

/**
 * `POST /api/v1/public/forms/:slug`。
 *
 * `consentTextSha256` を**クライアントに送り返させる**のが要点。
 * フォームを表示してから submit するまでの間に版が差し替わっていたら、
 * 受信者が読んだ文言と記録される文言がずれる。ハッシュ不一致は 409 にして
 * 「読んだものと違う同意」を記録しない。
 */
export const signupSubmitSchema = z
  .object({
    email: z.string().email().max(320),
    /** 追加欄の値。キーは版の `fields[].key` に一致すること。 */
    values: z.record(z.string().max(500)).default({}),
    consentTextSha256: consentTextHashSchema,
    /** チャレンジ（PoW / Turnstile）の応答。`challenge_mode` が off なら省略可。 */
    challengeToken: z.string().max(4000).optional(),
  })
  .strict();

/**
 * submit の応答。**成功・重複・チャレンジ要求を区別しない**（`accepted` に畳む）。
 *
 * 区別すると、フォームが「そのアドレスが登録済みか」を第三者に教えるオラクルになる。
 * 受信者本人には確認メールで伝わるので、画面で区別する必要が無い。
 */
export const signupSubmitResponseSchema = z
  .object({
    state: z.literal('accepted'),
    /** ダブルオプトインなら確認メールを送った旨を画面に出す。 */
    confirmationRequired: z.boolean(),
    redirectUrl: z.string().nullable(),
  })
  .strict();

export const signupSubmissionSchema = z
  .object({
    id,
    formVersionId: id,
    state: z.enum(SIGNUP_SUBMISSION_STATE),
    /** **72 時間で NULL 化される。** 未確認の生アドレスを持ち続けない（D23）。 */
    email: z.string().nullable(),
    submittedAt: isoDateTime,
    confirmSentAt: isoDateTime.nullable(),
    confirmedAt: isoDateTime.nullable(),
    expiresAt: isoDateTime,
  })
  .strict();

/** 未確認の生アドレスを NULL 化するまでの時間。`maintenance` の sweep が使う。 */
export const SIGNUP_EMAIL_REDACT_AFTER_HOURS = 72;

/* ================================================== オプトイン証跡（D23） */

/**
 * 同意の証跡。`optin_records.evidence` の中身をここで固定する。
 *
 * **生 IP と生 User-Agent を入れる場所が型として無い。** `ipPrefix` は
 * `/24`（IPv4）または `/48`（IPv6）に丸めた値で、UA は sha256 と family だけ。
 * 「後で必要になるかもしれないから全部取る」をやると、
 * 3 年保持（証跡保全）の対象に個人識別子が乗る。
 */
export const optinEvidenceSchema = z
  .object({
    /** 同意を取得したフォームの版。ここから同意文言の本体を引ける。 */
    formVersionId: id.optional(),
    /** 同意文言の sha256。**本文は複製しない**（D23）。 */
    consentTextSha256: consentTextHashSchema.optional(),
    /** 取得元 URL（フォームの設置ページ）。クエリは落として保存すること。 */
    sourceUrl: z.string().url().max(2000).optional(),
    /** 確認メールの Message-ID。ダブルオプトインの成立を後から追える唯一の手掛かり。 */
    confirmationMessageId: z.string().max(255).optional(),
    /** `/24`（IPv4）または `/48`（IPv6）に丸めた接続元。**生 IP は入れない**。 */
    ipPrefix: z.string().max(64).optional(),
    /** User-Agent の sha256。**生の UA は入れない**（fingerprint 材料になる）。 */
    userAgentSha256: z
      .string()
      .regex(/^sha256:[0-9a-f]{64}$/)
      .optional(),
    /** `Chrome` / `Safari` 等の大分類だけ。版番号も OS も入れない。 */
    userAgentFamily: z.string().max(40).optional(),
    /** 書面・対面での取得や名簿移行のときの説明。人間が読む欄。 */
    note: z.string().max(1000).optional(),
  })
  .strict();
export type OptinEvidence = z.infer<typeof optinEvidenceSchema>;

/**
 * オプトイン証跡そのもの。
 *
 * `method` ごとに**証跡として最低限必要なもの**が違うので、`superRefine` で
 * 分けて検査する。ここを緩くすると「method だけ `double_optin` と書いてある、
 * 中身が空の証跡」が量産され、証跡の存在が何も保証しなくなる。
 */
export const optinRecordInputSchema = z
  .object({
    method: z.enum(CONTACT_OPTIN_METHOD),
    occurredAt: isoDateTime,
    /** リスト単位の同意。組織全体の同意という概念は作らない。 */
    listId: id.optional(),
    evidence: optinEvidenceSchema,
  })
  .strict()
  .superRefine((value, ctx) => {
    const e = value.evidence;
    if (value.method === 'double_optin' && !e.confirmationMessageId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['evidence', 'confirmationMessageId'],
        message: 'ダブルオプトインでは確認メールの Message-ID が必要です',
      });
    }
    if (value.method === 'single_optin' && !e.formVersionId && !e.sourceUrl) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['evidence', 'sourceUrl'],
        message: 'フォームからの同意では、フォームの版か取得元 URL のいずれかが必要です',
      });
    }
    if (
      (value.method === 'offline' || value.method === 'imported_with_evidence') &&
      !e.note &&
      !e.sourceUrl
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['evidence', 'note'],
        message: '書面・移行による同意では、取得の経緯を説明する記録が必要です',
      });
    }
  });
export type OptinRecordInput = z.infer<typeof optinRecordInputSchema>;

/**
 * 証跡の保持期間（年）。**解除後も削除しない**（PD §5.1）。
 * 解除された宛先ほど「同意があったのか」を問われるので、
 * 解除と同時に証跡を消すと最も必要な場面で無くなる。
 */
export const OPTIN_EVIDENCE_RETENTION_YEARS = 3;

/**
 * IP を証跡用に丸める。IPv4 は `/24`、IPv6 は `/48`。
 *
 * 呼び出し側が丸め忘れる経路を作らないために shared に置く。
 * 判別できない入力は**保存しない**（`null`）。「壊れた値でも何か残す」より
 * 「証跡として使えない値は最初から持たない」ほうが安全。
 */
export function toIpPrefix(ip: string): string | null {
  const trimmed = ip.trim();
  if (trimmed.length === 0) return null;

  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(trimmed)) {
    const parts = trimmed.split('.').map((p) => Number(p));
    if (parts.some((p) => !Number.isInteger(p) || p < 0 || p > 255)) return null;
    return `${parts[0]}.${parts[1]}.${parts[2]}.0/24`;
  }

  if (trimmed.includes(':')) {
    // ゾーン ID（`%eth0`）を落としてから見る。
    const head = trimmed.split('%')[0] ?? trimmed;
    if (!/^[0-9a-fA-F:]+$/.test(head)) return null;

    // **`::` は必ず展開する。** 展開せずに先頭 3 グループを取ると
    // `fe80::1` が `fe80:0:1::/48` になり、**別のネットワークを指す接頭辞**が
    // 証跡に残る（`fe80::1` の /48 は `fe80:0:0::/48`）。
    const halves = head.split('::');
    if (halves.length > 2) return null;
    const left = (halves[0] ?? '').split(':').filter((g) => g.length > 0);
    const right = halves.length === 2 ? (halves[1] ?? '').split(':').filter((g) => g.length > 0) : [];
    const groups =
      halves.length === 2
        ? [...left, ...Array<string>(Math.max(0, 8 - left.length - right.length)).fill('0'), ...right]
        : left;
    if (groups.length !== 8) return null;
    if (groups.some((g) => !/^[0-9a-fA-F]{1,4}$/.test(g))) return null;

    // 先頭の 0 は落として正規化する（`00ab` と `ab` を別の接頭辞にしない）。
    const first3 = groups.slice(0, 3).map((g) => g.replace(/^0+(?=.)/, '').toLowerCase());
    return `${first3.join(':')}::/48`;
  }

  return null;
}
